import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../state'
import * as errorMessage from '../../error-message'

/**
 * Modal for assigning users to assistant agents with supported access flags.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.checkbox.Component,
    ds.input.select.multiple.Component,
    ds.overlay.centered.Component,
    ds.layout.widget.Component,
    errorMessage.Component,
  ],
  selector: 'atn-admin-agent-assignment-modal',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /**
   * Indicates whether the modal should display page context controls
   */
  public readonly hasPageContextSg = ngCore.input<boolean>(false, {
    alias: 'hasPageContext',
  })

  /** @see {@link state.admin.agentAssignment.Feature} */
  public readonly agentAssignmentFeatureEc = ngCore.inject(
    state.admin.agentAssignment.Feature,
  )

  /** @see {@link state.admin.appPages.Collection} */
  public readonly appPagesEc = ngCore.inject(state.admin.appPages.Collection)

  /**
   * List of users available for assignment
   */
  public usersSg = ngCore.computed(
    (users = this.agentAssignmentFeatureEc.selectors.users.asSignal()) =>
      users.map(user => ({ label: user.name, value: user.id })),
  )

  /**
   * List of all agents with human-readable labels
   */
  public agentsSg = ngCore.computed(() =>
    ents.core.agent.Display.ALL.map(agent => ({
      label: agent.name,
      value: agent.id,
    })),
  )

  /**
   * List of available application pages for page aliases
   */
  public appPagesSg = ngCore.computed(
    (
      appPages = this.appPagesEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) =>
      appPages.map(page => ({
        label: page.name,
        value: page.alias ?? page.id,
      })),
  )

  /**
   * List of currently selected page aliases
   */
  public pageAliasesSg = ngCore.computed(
    (
      pageAliases = this.agentAssignmentFeatureEc.selectors.pageAliases.asSignal(),
    ) => (pageAliases ? Array.from(pageAliases) : []),
  )

  /**
   * Whether agent is configured for non-prebuilt pages
   */
  public isForNonPrebuiltPagesSg = ngCore.computed(
    (
      isForNonPrebuiltPages = this.agentAssignmentFeatureEc.selectors.isForNonPrebuiltPages.asSignal(),
    ) => isForNonPrebuiltPages ?? false,
  )

  /** Discard all changes that have been edited */
  private _discardChanges(source: string): void {
    this.agentAssignmentFeatureEc.actions.reset.from(source).dispatch()
  }

  /**
   * Handle users selection change
   */
  public selectedUsersChangeHandler(items: readonly unknown[]): void {
    this.agentAssignmentFeatureEc.actions.setSelectedUserIds
      .from('AgentAssignment.Modal.Users.MultiSelect.Value.Changed')
      .dispatch({
        ids: items.filter(ents.core.admin.User.Id.isId).map(item => item),
      })
  }

  /**
   * Handle agents selection change
   */
  public selectedAgentsChangeHandler(items: readonly unknown[]): void {
    this.agentAssignmentFeatureEc.actions.setSelectedAgentIds
      .from('AgentAssignment.Modal.Agents.MultiSelect.Value.Changed')
      .dispatch({
        ids: items.filter(ents.core.agent.Agent.Id.isId),
      })
  }

  /**
   * Handle page aliases selection change
   */
  public pageAliasesChangeHandler(items: readonly unknown[]): void {
    this.agentAssignmentFeatureEc.actions.setPageAliases
      .from('AgentAssignment.Modal.PageAliases.MultiSelect.Value.Changed')
      .dispatch({
        pageAliases:
          items.length > 0
            ? new Set(items.filter((i): i is string => typeof i === 'string'))
            : undefined,
      })
  }

  /**
   * Handle non-prebuilt pages checkbox change
   */
  public isForNonPrebuiltPagesChangeHandler(isSelected: boolean): void {
    this.agentAssignmentFeatureEc.actions.setIsForNonPrebuiltPages
      .from('AgentAssignment.Modal.NonPrebuiltPages.Checkbox.Changed')
      .dispatch({
        isForNonPrebuiltPages: isSelected || undefined,
      })
  }

  /**
   * Returns whether the access flag is selected.
   */
  public isAccessFlagSelected(
    access: ents.core.admin.Access,
    flag: ents.core.admin.Access.Flag,
  ): boolean {
    return ents.core.admin.Access.allows(access, flag)
  }

  /** Checkbox value change handler */
  public accessValueChangeHandler(
    isSelected: boolean,
    flag: ents.core.admin.Access.Flag,
  ): void {
    const currentAccess =
      this.agentAssignmentFeatureEc.selectors.access.asSignal()
    const access = isSelected
      ? ents.core.admin.Access.add(currentAccess, flag)
      : ents.core.admin.Access.remove(currentAccess, flag)
    this.agentAssignmentFeatureEc.actions.setAccess
      .from('AgentAssignment.Modal.Access.Checkbox.Changed')
      .dispatch({ access })
  }

  /** Submit changes handler */
  public saveClickHandler(): void {
    this.agentAssignmentFeatureEc.actions.assignAgentAccess
      .from('AgentAssignment.Modal.Save.Button.Clicked')
      .dispatch({
        assignments:
          this.agentAssignmentFeatureEc.selectors.assignments.asSignal(),
      })
  }

  /**
   * Cancel button handler
   */
  public cancelButtonClickHandler(): void {
    this._discardChanges('AgentAssignment.Modal.Cancel.Button.Clicked')
  }

  /**
   * Close icon handler
   */
  public closeIconClickHandler(): void {
    this._discardChanges('AgentAssignment.Modal.Close.Icon.Clicked')
  }
}
